import React from 'react';
import Tool, {
  type ToolArgs,
  type ToolArgsWithController,
} from '../../../tools/Tool';
import TableUtils from '../TableUtils';
import { type Editor, type Path, Transforms } from 'slate';
import { getTableStructureFromSelection } from '../TableStructure';
import ColorUtils from '../../../util/color';
import TableAlternateRowColorToolbarComponent from '../components/TableAlternateRowColorToolbarComponent';

const getCurrentColor = (editor: Editor): undefined | string => {
  const tableElement = TableUtils.currentTableElement(editor);
  if (!tableElement) return;

  const color = tableElement.table_alternateRowColor;

  if (color) {
    return ColorUtils.mapColorFromColorPickerToDarkMode(color);
  }
};

const setAlternateRowColor = (editor: Editor, path: Path, color?: string) => {
  if (color) {
    Transforms.setNodes(
      editor,
      { table_alternateRowColor: color },
      { at: path }
    );
  } else {
    Transforms.unsetNodes(editor, 'table_alternateRowColor', {
      at: path,
    });
  }
};

class ToolTableAlternateRowColor extends Tool {
  static toolName = 'Table alternate row color';

  static action(args: ToolArgs) {
    const { editor } = args;

    const tableStructure = getTableStructureFromSelection(editor);
    if (!tableStructure) return;

    const tablePath = TableUtils.currentTablePath(editor);
    if (!tablePath) return;

    setAlternateRowColor(editor, tablePath, args.color);
  }

  static available(toolArgs: ToolArgsWithController) {
    const { editor } = toolArgs;
    const tableElement = TableUtils.currentTableElement(editor);
    if (!tableElement) return false;

    return Boolean(tableElement.table_alternateRows);
  }

  static renderToolbarComponent(toolArgs: ToolArgsWithController): JSX.Element {
    const { editor, controller } = toolArgs;

    const setRowColor = (color?: string) => {
      ToolTableAlternateRowColor.action({
        editor,
        controller,
        color,
      });
    };

    return (
      <TableAlternateRowColorToolbarComponent
        editor={editor}
        getRowColor={() => getCurrentColor(editor)}
        setRowColor={setRowColor}
        active={this.isActive(toolArgs)}
        enabled={this.isEnabled(toolArgs)}
      />
    );
  }
}

export default ToolTableAlternateRowColor;
